import { dateFormats, DateObject, type TranslationIDs } from '@capsicum/common';
// eslint-disable-next-line no-restricted-imports

/**
 * Get the remaining time values for unlocking a digital collectible.
 *
 * @param {DateObject} date - The target date and time for unlocking the asset.
 * @returns {Object|undefined} An object containing the remaining time values.
 * @property {TranslationIDs} translationKey - A translation key indicating the time unit ('days', 'hours', 'lessThan1Hour').
 * @property {number} value - The remaining time value as number.
 *
 * @example
 * // Usage
 * const dateToUnlock = new Date('2023-12-31T23:59:59');
 * const remainingTime = getUnlockingAvailableAtValues(dateToUnlock);
 *
 * // Result
 * // If the date is in the past:
 * // undefined
 *
 * // If there are more than 1 day remaining:
 * // { translationKey: 'days', value: '5' }
 *
 * // If there are more than 1 hour but less than 1 day remaining:
 * // { translationKey: 'hours', value: '18' }
 *
 * // If there is less than 1 hour remaining:
 * // { translationKey: 'lessThan1Hour', value: 0 }
 */

export const getUnlockingAvailableAtValues = (
	date = new DateObject()
): { translationKey: TranslationIDs; value: number } => {
	const { diff } = dateFormats;
	const remainingDays = diff(date, new DateObject(), 'days');
	const remainingHours = diff(date, new DateObject(), 'hours');

	if (remainingDays > 0) {
		return { translationKey: 'digitalCollectibles.collectibleCard.days', value: remainingDays };
	} else if (remainingHours > 1) {
		return { translationKey: 'digitalCollectibles.collectibleCard.hours', value: remainingHours };
	} else {
		return { translationKey: 'digitalCollectibles.collectibleCard.lessThan1Hour', value: 0 };
	}
};
